'use client';

import { usePathname } from 'next/navigation';
import React from 'react';
import styles from './TradeSteps.module.scss';

// ステップの状態を明確に定義
export const TradeStepStatus = {
  PENDING: { value: 'pending', label: '未開始' },
  CURRENT: { value: 'current', label: '進行中' },
  COMPLETED: { value: 'completed', label: '完了' },
} as const;

export type TradeStepStatusType = keyof typeof TradeStepStatus;

// ステップ定義を定数化（URLパスとマッピング）
export const TRADE_STEPS = [
  { id: 'select-tradeable-prizes', label: '交換景品の選択', pathKeyword: 'select-tradeable-prizes' },
  { id: 'select-desired-prizes', label: '希望景品の選択', pathKeyword: 'select-desired-prizes' },
  { id: 'confirm', label: '確認', pathKeyword: 'confirm' },
  { id: 'complete', label: '完了', pathKeyword: 'complete' },
] as const;

function TradeSteps() {
  const pathname = usePathname();

  // 現在のステップをURLから判定
  const getCurrentStepIndex = (): number => {
    // completeまたはcancelCompleteページの場合は完了ステップ（インデックス3）
    if (pathname.includes('complete') || pathname.includes('cancelComplete')) {
      return 3; // completeステップのインデックス
    }

    // historyページ（一覧）の場合は交換景品の選択ステップ（インデックス0）
    if (pathname.includes('/history') && !pathname.includes('cancelComplete') && !pathname.includes('/history/')) {
      return 0; // select-tradeable-prizesステップのインデックス
    }

    // history/[tradeRequestId]ページの場合は希望景品の選択ステップ（インデックス1）
    if (pathname.includes('/history/') && !pathname.includes('cancelComplete')) {
      return 1; // select-desired-prizesステップのインデックス
    }

    const stepIndex = TRADE_STEPS.findIndex((step) => pathname.includes(step.pathKeyword));
    return stepIndex !== -1 ? stepIndex : 0; // デフォルトは最初のステップ
  };

  const currentStepIndex = getCurrentStepIndex();

  // ステップの状態を判定する関数
  const getStepStatus = (index: number): TradeStepStatusType => {
    if (index < currentStepIndex) return 'COMPLETED';
    if (index === currentStepIndex) return 'CURRENT';
    return 'PENDING';
  };

  // 履歴関連ページ（history、history/[tradeRequestId]、cancelComplete）かどうかをチェック
  const isHistoryRelatedPage = pathname.includes('/history');

  const steps = TRADE_STEPS.map((step, index) => {
    let label: string = step.label;

    // 履歴関連ページでのラベル変更
    if (isHistoryRelatedPage) {
      if (step.id === 'select-tradeable-prizes') {
        label = '交換履歴選択';
      } else if (step.id === 'select-desired-prizes') {
        label = '交換の取り消し';
      }
    }

    return {
      ...step,
      label,
      status: getStepStatus(index),
    };
  });

  return (
    <div className={styles.container}>
      {steps.map((step, index) => {
        const isActive = step.status === 'CURRENT';
        return (
          <div key={step.id} className={`${styles.item} ${isActive ? styles.active : ''}`}>
            <div className={styles.labelContent}>
              <span className={styles.labelNumber}>{index + 1}. </span>
              <span className={styles.labelText}>{step.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TradeSteps;
